import { IdxStep, AuthenticatorKey, MGMAuth } from '@mgmresorts/mgm-auth-js-sdk';

import { captureException } from '../../monitoring/logrocket';
import { EventAttributes } from '../analytics/types';
import { canUsePasskey, getPasskeySupportDetails } from '../passkey';

export interface PasskeySignInOptions {
  email: string;
  authClient: MGMAuth;
  trackEvent?: (
    name: string,
    description: string,
    action: string,
    attributes?: Omit<EventAttributes, 'title'>,
    immediate?: boolean,
  ) => Promise<void>;
  redirectToApp?: () => void;
}

export interface PasskeySignInResult {
  success: boolean;
  error?: string;
}

/**
 * Reusable utility for signing in with passkey
 * Extracted from SignIn component for use across multiple components
 */
export const signInWithPasskey = async (options: PasskeySignInOptions): Promise<PasskeySignInResult> => {
  const { email, authClient, trackEvent, redirectToApp } = options;

  try {
    if (!email) {
      throw new Error('Email is required for passkey authentication');
    }

    // CRITICAL: Check device passkey support BEFORE attempting ANY authentication
    console.log('🔐 Checking passkey support before authentication...');
    const deviceSupportsPasskeys = await canUsePasskey();
    const supportDetails = await getPasskeySupportDetails();

    console.log('🔐 Device passkey support:', deviceSupportsPasskeys);
    console.log('🔐 Passkey support details:', supportDetails);

    // STOP IMMEDIATELY if device doesn't support passkeys - DO NOT proceed with IDX flow
    if (!deviceSupportsPasskeys) {
      console.log('🚫 Device does not support passkeys, showing error message instead of attempting authentication');
      throw new Error(
        `Passkeys are not supported on this ${supportDetails.platform} device using ${supportDetails.browser}. Please sign in with password instead.`,
      );
    }

    // Additional check: Verify WebAuthn is actually available
    if (!window.PublicKeyCredential) {
      console.log('🚫 PublicKeyCredential not available, preventing passkey authentication');
      throw new Error('Passkeys are not available in this browser. Please sign in with password instead.');
    }

    if (trackEvent) {
      await trackEvent('Sign in with passkey', 'Sign in with passkey', 'signinwithpasskey', {}, true);
    }

    console.log('✅ Device supports passkeys, proceeding with authentication...');
    // Only proceed with IDX flow if device supports passkeys
    await authClient.idx.startAuthenticate(email);
    await authClient.idx.stepHandlers[IdxStep.Identify].Identify(email);

    // Get the passkey auth handler and check if passkey is available for this account
    const passkeyAuthHandler = authClient.idx.stepHandlers[IdxStep.AuthPasskey];

    if (!passkeyAuthHandler.canSelectAuth(AuthenticatorKey.WEBAUTHN)) {
      console.log('🚫 Passkey not available for this account');
      throw new Error('Passkey is not available for this account. Please use password authentication.');
    }

    console.log('🔐 Starting passkey authentication...');
    // Perform passkey authentication with specific error handling for missing credentials
    try {
      await passkeyAuthHandler.passkeyAuth();
    } catch (passkeyError) {
      console.log('🚨 Passkey authentication failed:', passkeyError);
      // Handle the specific case where no passkeys are registered locally
      if (passkeyError instanceof Error && passkeyError.name === 'NotAllowedError') {
        // This usually means no passkeys are registered for this domain
        throw new Error(
          'No passkeys are registered for this account on this device. Please enroll a passkey first or sign in with password.',
        );
      } else if (passkeyError instanceof Error && passkeyError.name === 'AbortError') {
        throw new Error('Passkey authentication was cancelled. Please try again or sign in with password.');
      } else if (passkeyError instanceof Error && passkeyError.name === 'NotSupportedError') {
        throw new Error('Passkeys are not supported on this device. Please sign in with password.');
      } else {
        // Re-throw the original error to be handled by outer catch block
        throw passkeyError;
      }
    }

    // Check if authentication was successful
    if (authClient.idx.nextIdxStep === IdxStep.Success) {
      authClient.idx.stepHandlers[IdxStep.Success].setTokens();
      console.log('✅ Passkey authentication successful!');

      if (trackEvent) {
        await trackEvent('identity – passkey auth success', 'passkey auth success', 'passkeyauthsuccess', {}, true);
      }

      if (redirectToApp) {
        redirectToApp();
      }

      return { success: true };
    } else {
      console.log('❌ Passkey authentication did not complete successfully');
      throw new Error('Passkey authentication did not complete successfully.');
    }
  } catch (error) {
    console.error('🚨 Passkey authentication error:', error);

    let errorMessage = 'Passkey authentication failed. ';
    if (error instanceof Error) {
      if (error.message.includes('not supported')) {
        errorMessage = 'Passkeys are not supported on this device. Please sign in with password.';
      } else if (error.message.includes('not available')) {
        errorMessage = 'No passkey is set up on this device. Please sign in using another verification method.';
      } else if (error.message.includes('cancelled')) {
        errorMessage = 'Passkey authentication was cancelled. Please try again or sign in with password.';
      } else if (error.message.includes('Please sign in with password')) {
        errorMessage = error.message;
      } else {
        errorMessage += 'Please sign in with password instead.';
      }
    }

    // Log error for monitoring
    captureException(error as Error, 'passkey-signin', { email });

    return { success: false, error: errorMessage };
  }
};
